import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../contexts/StoreContext';
import { getInventoriesForOrdering } from '../services/ordersService';
import api from '../services/api';
import { FaSpinner, FaSearch, FaTimes, FaChartBar } from 'react-icons/fa';
import './css_pages/VarianceReport.css';

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const year  = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day   = String(date.getUTCDate()).padStart(2, '0');
  return `${month}/${day}/${year}`;
};

const fmt = (val, decimals = 2) => {
  const n = parseFloat(val);
  if (isNaN(n)) return (0).toFixed(decimals);
  return n.toFixed(decimals);
};

const VarianceReport = () => {
  const { selectedStore } = useStore();

  const [inventories, setInventories]             = useState([]);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [rows, setRows]                           = useState([]);
  const [wsTotalAll, setWsTotalAll]               = useState(0);
  const [prevDate, setPrevDate]                   = useState(null);
  const [loading, setLoading]                     = useState(false);
  const [hasLoaded, setHasLoaded]                 = useState(false);
  const [groupBy, setGroupBy]                     = useState('none'); // none | type | category
  const [search, setSearch]                       = useState('');

  // ── Fetch de inventarios (por fecha) ──────────────────────────────────────
  const fetchInventories = useCallback(async () => {
    if (!selectedStore) return;
    try {
      setLoading(true);
      const r = await getInventoriesForOrdering(selectedStore.id);
      if (r.success) setInventories(r.data);
    } catch (error) {
      console.error('Error fetching inventories:', error);
      alert('Error loading inventories');
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      fetchInventories();
      setRows([]);
      setWsTotalAll(0);
      setSelectedInventory(null);
      setPrevDate(null);
      setHasLoaded(false);
      setSearch('');
    }
  }, [selectedStore, fetchInventories]);

  // ── Al elegir un inventario: pide el reporte al endpoint dedicado ─────────
  const handleInventoryChange = async (e) => {
    const inventoryId = e.target.value;
    if (!inventoryId) {
      setSelectedInventory(null);
      setRows([]);
      setWsTotalAll(0);
      setPrevDate(null);
      setHasLoaded(false);
      return;
    }
    try {
      setLoading(true);
      const inventory = inventories.find(inv => inv.id_inventories === parseInt(inventoryId));
      setSelectedInventory(inventory);

      const res = await api.get('/reports/variance-report', {
        params: { inventoryId: parseInt(inventoryId), storeId: selectedStore.id }
      });

      if (res.data && res.data.success) {
        const payload = res.data.data;
        setRows(payload.rows || []);
        setWsTotalAll(parseFloat(payload.ws_total) || 0);
        setPrevDate(payload.prev_date);
        setHasLoaded(true);
      }
    } catch (error) {
      console.error('Error loading variance report:', error);
      alert('Error loading variance report');
    } finally {
      setLoading(false);
    }
  };

  // ── Filtro por búsqueda ───────────────────────────────────────────────────
  const filtered = rows.filter(p => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return (p.product_name || '').toLowerCase().includes(s)
        || (p.product_code || '').toLowerCase().includes(s);
  });

  const filteredWs  = filtered.reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0);
  const totalCount  = filtered.length;
  const isFiltering = search.trim().length > 0;

  const varianceClass = (v) => {
    const n = parseFloat(v) || 0;
    if (n >  0.05) return 'vr-var-pos';
    if (n < -0.05) return 'vr-var-neg';
    return '';
  };

  // ── Agrupado (None / Product Type / Category) ─────────────────────────────
  const buildGroups = () => {
    const key = groupBy === 'type' ? 'product_type_name' : 'category_name';
    const fallback = groupBy === 'type' ? 'Other' : 'Uncategorized';
    const map = {};
    filtered.forEach(p => {
      const k = p[key] || fallback;
      if (!map[k]) map[k] = [];
      map[k].push(p);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const COL_COUNT = 10;

  const rawParen = (value, decimals = 2) => (
    <span style={{ display: 'block', color: '#64748b', fontSize: '11px' }}>({fmt(value, decimals)})</span>
  );

  const renderRow = (p) => {
    const isCase = p.order_by === 'Case';
    const cs = parseFloat(p.case_size) || 1;
    return (
    <tr key={p.id_product}>
      <td className="vr-name-cell">{p.product_name}</td>
      <td>{p.product_code || '-'}</td>
      <td>{p.container_size} {p.container_unit}</td>
      <td className="vr-num">
        {isCase ? (<><span style={{ display: 'block' }}>{fmt(p.opening_last_inv)}</span>{rawParen(p.opening_last_inv * cs)}</>) : fmt(p.opening_last_inv)}
      </td>
      <td className="vr-num">
        {isCase ? (<><span style={{ display: 'block' }}>{fmt(p.purchase)}</span>{rawParen(p.purchase * cs)}</>) : fmt(p.purchase)}
      </td>
      <td className="vr-num">{fmt(p.sold)}</td>
      <td className={`vr-num ${varianceClass(p.variance)}`}>
        {isCase ? (<><span style={{ display: 'block' }}>{fmt(p.variance)}</span>{rawParen(p.variance * cs)}</>) : fmt(p.variance)}
      </td>
      <td className="vr-num">
        {isCase ? (<><span style={{ display: 'block' }}>{fmt(p.stock_on_hand)}</span>{rawParen(p.stock_on_hand * cs)}</>) : fmt(p.stock_on_hand)}
      </td>
      <td className="vr-num">${fmt(p.unit_price)}</td>
      <td className="vr-num vr-total">${fmt(p.total)}</td>
    </tr>
    );
  };

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!selectedStore) {
    return (
      <div className="vr-page">
        <div className="vr-page-header"><h1 className="vr-page-title"><FaChartBar /> Variance Report</h1></div>
        <div className="vr-empty"><p>Please select a store to view the variance report.</p></div>
      </div>
    );
  }

  return (
    <div className="vr-page">

      {/* HEADER */}
      <div className="vr-page-header vr-sticky-header">
        <div className="vr-header-left">
          <h1 className="vr-page-title"><FaChartBar /> Variance Report</h1>
          <span className="vr-store-badge">{selectedStore.name}</span>
        </div>
        {hasLoaded && (
          <div className="vr-ws-total">
            <span className="vr-ws-total-label">WS Total</span>
            <span className="vr-ws-total-value">${fmt(wsTotalAll)}</span>
          </div>
        )}
      </div>

      {/* CONTROLS */}
      <div className="vr-controls">
        <div className="vr-control-group">
          <label htmlFor="vr-inv-select">Select Inventory (by date):</label>
          <select
            id="vr-inv-select"
            onChange={handleInventoryChange}
            disabled={loading || inventories.length === 0}
            className="vr-select"
            value={selectedInventory?.id_inventories || ''}
          >
            <option value="">-- Select an inventory --</option>
            {inventories.map(inv => (
              <option key={inv.id_inventories} value={inv.id_inventories}>
                {formatDate(inv.inventory_date)}
              </option>
            ))}
          </select>
        </div>

        {selectedInventory && (
          <span className="vr-date-range">
            {prevDate
              ? <>Purchase period: <strong>{formatDate(prevDate)}</strong> → <strong>{formatDate(selectedInventory.inventory_date)}</strong></>
              : <>No previous inventory — Opening &amp; Purchase show 0</>}
          </span>
        )}
      </div>

      {loading && (
        <div className="vr-loading"><FaSpinner className="vr-spinner" /><p>Loading…</p></div>
      )}

      {!loading && hasLoaded && (
        <>
          {/* TOOLBAR: group-by + search */}
          <div className="vr-toolbar">
            <div className="vr-groupby">
              <span className="vr-groupby-label">Group by:</span>
              <button className={`vr-toggle-btn ${groupBy === 'none' ? 'active' : ''}`} onClick={() => setGroupBy('none')} type="button">None</button>
              <button className={`vr-toggle-btn ${groupBy === 'type' ? 'active' : ''}`} onClick={() => setGroupBy('type')} type="button">Product Type</button>
              <button className={`vr-toggle-btn ${groupBy === 'category' ? 'active' : ''}`} onClick={() => setGroupBy('category')} type="button">Category</button>
            </div>

            <div className="vr-search">
              <FaSearch className="vr-search-icon" />
              <input
                type="text"
                placeholder="Search by name or code…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <button className="vr-search-clear" onClick={() => setSearch('')} type="button"><FaTimes /></button>}
            </div>

            <div className="vr-count-badge">{totalCount} product(s)</div>
          </div>

          {/* TABLE */}
          <div className="vr-table-wrap">
            <table className="vr-table">
              <thead>
                <tr>
                  <th>Product<br/>Name</th>
                  <th>Product<br/>Code</th>
                  <th>Container<br/>Size</th>
                  <th title="Stock from previous inventory">Opening<br/>Inv</th>
                  <th title="Purchases between previous and selected inventory">Purchase</th>
                  <th title="Units sold (not yet available)">Sold</th>
                  <th title="Stock on Hand − (Opening + Purchase) + Sold">Variance</th>
                  <th>Stock on<br/>Hand</th>
                  <th>Unit<br/>Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={COL_COUNT} className="vr-no-results">No products found</td></tr>
                ) : groupBy === 'none' ? (
                  filtered.map(renderRow)
                ) : (
                  buildGroups().map(([groupName, groupRows]) => {
                    const groupWs = groupRows.reduce((s, p) => s + (parseFloat(p.total) || 0), 0);
                    return (
                      <React.Fragment key={groupName}>
                        <tr className="vr-group-row">
                          <td colSpan={COL_COUNT}><strong>{groupName}</strong> <span className="vr-group-count">({groupRows.length})</span></td>
                        </tr>
                        {groupRows.map(renderRow)}
                        <tr className="vr-group-subtotal">
                          <td colSpan={COL_COUNT - 1} className="vr-subtotal-label">{groupName} subtotal:</td>
                          <td className="vr-num vr-total">${fmt(groupWs)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="vr-grand-total-row">
                    <td colSpan={COL_COUNT - 1} className="vr-grand-total-label">
                      {isFiltering ? `Filtered WS (${totalCount} products):` : `WS Total (${totalCount} products):`}
                    </td>
                    <td className="vr-num vr-grand-total-value">${fmt(isFiltering ? filteredWs : wsTotalAll)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}

      {!loading && !hasLoaded && (
        <div className="vr-empty">
          <p>Select an inventory date above to generate the variance report.</p>
        </div>
      )}
    </div>
  );
};

export default VarianceReport;